import { FC, useCallback, useEffect, useState } from 'react';
import { Loader } from '../../../components';
import SortedTable from '../../../components/sorted-table/SortedTable';
import { OpenEyeSVG, TrashCanSVG } from '../../../components/svgs';
import AddDocumentModal from './AddDocumentModal';
import ConfirmModal from '../../user-profile/user-edit/confirm-modal/ConfirmModal';
import { deleteAnimalDocument, getAnimalDocuments } from '../../../api/animalsApi';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { requestAnimalDocumentSasUrl } from '../../../services/animal.service';
import { showToast } from '../../../services/toast.service';
import { formatDate } from '../../../utils/dateHandler';
import { AnimalDocument } from '../../../types/interfaces/animal.interfaces';

type AnimalDocumentsProps = {
  animalId?: string;
};

const getDocumentTypeLabel = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toUpperCase() ?? '';
  return extension === 'JPEG' ? 'JPG' : extension;
};

const getDocumentDisplayName = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
};

const getDocumentDescription = (documentItem: AnimalDocument): string => documentItem.description || '-';

const getDocumentUpdatedOn = (documentItem: AnimalDocument): string | Date | undefined => {
  return documentItem.createdDatetime;
};

const MAX_DOCUMENT_COUNT = 10;

const AnimalDocuments: FC<AnimalDocumentsProps> = ({ animalId }) => {
  const [documents, setDocuments] = useState<AnimalDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<AnimalDocument>();

  const handleGetDocuments = useCallback(async () => {
    if (!animalId) {
      return;
    }

    setLoadingDocuments(true);
    try {
      const { data } = await getAnimalDocuments(animalId);
      setDocuments(data);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
    setLoadingDocuments(false);
  }, [animalId]);

  useEffect(() => {
    handleGetDocuments();
  }, [handleGetDocuments]);

  const handleViewDocument = async (documentItem: AnimalDocument) => {
    if (!animalId || !documentItem.accessToken) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    const documentWindow = window.open('about:blank', '_blank');

    try {
      const url = await requestAnimalDocumentSasUrl(animalId, documentItem.attachmentId, documentItem.accessToken);
      if (!url) {
        documentWindow?.close();
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        return;
      }

      if (documentWindow) {
        documentWindow.opener = null;
        documentWindow.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      documentWindow?.close();
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleAddDocumentClick = () => {
    if (documents.length >= MAX_DOCUMENT_COUNT) {
      showToast.error(toastMessages.UPLOAD_MAX_5_DOCUMENTS);
      return;
    }

    setIsAddModalOpen(true);
  };

  const handleDeleteDocument = async () => {
    if (!animalId || !documentToDelete) return;

    try {
      await deleteAnimalDocument(animalId, documentToDelete.attachmentId);
      showToast.success(toastMessages.DOCUMENT_DELETED);
      setDocumentToDelete(undefined);
      handleGetDocuments();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const documentActions = (documentItem: AnimalDocument) => {
    return (
      <div className="document-actions">
        <button
          type="button"
          className="document-action-button"
          aria-label={BUTTON_CONSTANTS.VIEW}
          onClick={() => handleViewDocument(documentItem)}>
          <OpenEyeSVG />
        </button>
        <button
          type="button"
          className="document-action-button"
          aria-label={BUTTON_CONSTANTS.DELETE}
          onClick={() => setDocumentToDelete(documentItem)}>
          <TrashCanSVG />
        </button>
      </div>
    );
  };

  const renderDocumentName = (documentItem: AnimalDocument) => (
    <div className="document-name">
      <span className="document-type">{getDocumentTypeLabel(documentItem.name)}</span>
      <span>{getDocumentDisplayName(documentItem.name)}</span>
    </div>
  );

  const getMobileDocumentsView = () => {
    if (loadingDocuments) {
      return <Loader loaderSize="small" simple />;
    }

    if (documents.length === 0) {
      return <p className="no-documents">{ANIMAL_CONSTANTS.NO_DOCUMENTS}</p>;
    }

    return documents.map(documentItem => {
      const updatedOn = getDocumentUpdatedOn(documentItem);

      return (
        <div className="card max-width" key={documentItem.attachmentId}>
          <div className="animal-document-card">
            <div className="animal-document-content">
              <div className="card-content-section">
                <div className="document-content">
                  <label>{TABLE_HEADER_CONSTANTS.DOCUMENT_NAME}:</label>
                  {renderDocumentName(documentItem)}
                </div>
                <div className="document-content">
                  <label>{TABLE_HEADER_CONSTANTS.UPDATED_ON}:</label>
                  <label>{formatDate(updatedOn)}</label>
                </div>
              </div>
              <div className="card-content-section">
                <div className="document-content document-content-full">
                  <label>{TABLE_HEADER_CONSTANTS.DESCRIPTION}:</label>
                  <label>{getDocumentDescription(documentItem)}</label>
                </div>
              </div>
            </div>
            <div className="action-button">{documentActions(documentItem)}</div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="animal-documents">
      <div className="animal-documents-header">
        <h2>{ANIMAL_CONSTANTS.DOCUMENTS}</h2>
        <button type="button" className="button blue small add-document-button" onClick={handleAddDocumentClick}>
          {BUTTON_CONSTANTS.ADD_DOCUMENT}
        </button>
      </div>

      <div className="desk-animal-documents-table">
        <SortedTable
          headers={[
            { displayName: TABLE_HEADER_CONSTANTS.DOCUMENT_NAME },
            { displayName: TABLE_HEADER_CONSTANTS.DESCRIPTION },
            { displayName: TABLE_HEADER_CONSTANTS.UPDATED_ON },
            { displayName: TABLE_HEADER_CONSTANTS.ACTION },
          ]}
          data={documents.map(documentItem => {
            const updatedOn = getDocumentUpdatedOn(documentItem);

            return [
              {
                content: renderDocumentName(documentItem),
                sortValue: documentItem.name,
              },
              { content: getDocumentDescription(documentItem) },
              {
                content: formatDate(updatedOn),
                sortValue: updatedOn,
              },
              { content: documentActions(documentItem) },
            ];
          })}
          loading={loadingDocuments}
          noResultsMessage={ANIMAL_CONSTANTS.NO_DOCUMENTS}
        />
      </div>

      <div className="xs-animal-documents-table">{getMobileDocumentsView()}</div>

      <AddDocumentModal
        animalId={animalId}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onUploaded={handleGetDocuments}
      />

      <ConfirmModal
        modalHeader={ANIMAL_CONSTANTS.DELETE_DOCUMENT}
        modalBody={ANIMAL_CONSTANTS.DELETE_DOCUMENT_CONFIRM}
        isOpen={Boolean(documentToDelete)}
        firstBtn={
          <button type="button" className="small inverted blue button" onClick={() => setDocumentToDelete(undefined)}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
        }
        secondBtn={
          <button type="button" className="small red button" onClick={handleDeleteDocument}>
            {BUTTON_CONSTANTS.DELETE}
          </button>
        }
      />
    </div>
  );
};

export default AnimalDocuments;
